import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

// ── Tab panel styles ──────────────────────────────────────────────────────────
export const tabPanelStyles = [
  hostReset,
  css`
    :host {
      display: block;
    }
    /* Hidden when not active — parent sets [active] attribute */
    :host(:not([active])) {
      display: none !important;
    }
  `,
];

// ── Tabs container styles ─────────────────────────────────────────────────────
export const tabsStyles = [
  hostReset,
  css`
    :host {
      display: block;
    }

    /* ── Root layout ──────────────────────────────────────────────────────── */
    .uwc-tabs {
      display:        flex;
      flex-direction: column;
    }

    /* ── Nav wrap: holds optional scroll buttons + nav container ─────────── */
    .uwc-tabs__nav-wrap {
      display:     flex;
      align-items: center;
      position:    relative;
      border-bottom: 1px solid var(--uwc-tabs-nav-border, ${t.border});
    }

    /* ── Nav container: clips overflow, positions the ink bar ────────────── */
    .uwc-tabs__nav-container {
      flex:      1;
      min-width: 0;
      position:  relative;
      overflow:  hidden;
    }

    /* ── Tab list ────────────────────────────────────────────────────────── */
    .uwc-tabs__nav {
      display:     flex;
      align-items: stretch;
      overflow-x:  auto;
      /* Hide scrollbar — navigation is via JS scroll buttons */
      scrollbar-width:    none;
      -ms-overflow-style: none;
    }
    .uwc-tabs__nav::-webkit-scrollbar { display: none; }

    /* ── Individual tab (uses role="tab" on a <div> so we can nest a
         <button> for the close action without nesting interactive HTML) ─── */
    .uwc-tabs__tab {
      position:    relative;
      display:     inline-flex;
      align-items: center;
      gap:         var(--uwc-tabs-tab-gap, 0.375rem);
      padding:     var(--uwc-tabs-tab-padding, 0.625rem 1rem);
      cursor:      pointer;
      white-space: nowrap;
      user-select: none;
      outline:     none;

      color:       var(--uwc-tabs-tab-color,       ${t.textSecondary});
      font-size:   var(--uwc-tabs-tab-font-size,    ${t.fontSizeMd});
      font-weight: var(--uwc-tabs-tab-font-weight,  ${t.fontWeightMedium});
      line-height: 1.4;

      transition:
        color      ${t.durationBase} ease,
        background ${t.durationBase} ease;
    }

    .uwc-tabs__tab:hover:not(.uwc-tabs__tab--disabled):not(.uwc-tabs__tab--active) {
      color:      var(--uwc-tabs-tab-hover-color, ${t.text});
      background: var(--uwc-tabs-tab-hover-bg,    ${t.hoverBg});
    }

    .uwc-tabs__tab:focus-visible {
      outline:        2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
      border-radius:  ${t.radiusSm};
    }

    .uwc-tabs__tab--active {
      color:       var(--uwc-tabs-tab-active-color,       ${t.primary});
      font-weight: var(--uwc-tabs-tab-active-font-weight, ${t.fontWeightSemibold});
    }

    .uwc-tabs__tab--disabled {
      color:          var(--uwc-tabs-tab-disabled-color, ${t.textDisabled});
      cursor:         not-allowed;
      pointer-events: none;
    }

    /* ── Tab label ───────────────────────────────────────────────────────── */
    .uwc-tabs__tab-label {
      flex:          1;
      min-width:     0;
      overflow:      hidden;
      text-overflow: ellipsis;
    }

    /* ── Tab icon ────────────────────────────────────────────────────────── */
    .uwc-tabs__tab-icon {
      flex-shrink: 0;
    }

    /* ── Close button ─────────────────────────────────────────────────────
       Sits inside the role="tab" <div> — valid because <div> is not an
       interactive element. Focusable via mouse; keyboard uses Delete key.
    ──────────────────────────────────────────────────────────────────────── */
    .uwc-tabs__close-btn {
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      width:           18px;
      height:          18px;
      margin-left:     0.125rem;
      padding:         0;
      border:          none;
      border-radius:   ${t.radiusSm};
      background:      transparent;
      cursor:          pointer;
      color:           currentColor;
      opacity:         0.45;
      transition:
        opacity    ${t.durationBase} ease,
        background ${t.durationBase} ease;
    }
    .uwc-tabs__close-btn:hover {
      opacity:    1;
      background: color-mix(in oklab, currentColor 14%, transparent);
    }

    /* ── Animated ink bar ─────────────────────────────────────────────────
       Position is set by JS after measuring the active tab's offsetLeft
       and offsetWidth. Transitions produce the sliding effect.
    ──────────────────────────────────────────────────────────────────────── */
    .uwc-tabs__ink-bar {
      position:      absolute;
      bottom:        0;
      left:          0;
      height:        var(--uwc-tabs-ink-bar-size,  2px);
      width:         0;
      background:    var(--uwc-tabs-ink-bar-color, ${t.primary});
      border-radius: ${t.radiusFull};
      pointer-events: none;
      transition:
        left   220ms ${t.easingStandard},
        width  220ms ${t.easingStandard},
        top    220ms ${t.easingStandard},
        height 220ms ${t.easingStandard};
    }

    /* ── Scroll buttons ──────────────────────────────────────────────────── */
    .uwc-tabs__scroll-btn {
      flex-shrink:     0;
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      width:           28px;
      height:          100%;
      min-height:      38px;
      border:          none;
      padding:         0;
      background:      var(--uwc-tabs-scroll-btn-bg, ${t.surface});
      color:           var(--uwc-tabs-scroll-btn-color, ${t.textSecondary});
      cursor:          pointer;
      transition:
        color      ${t.durationBase} ease,
        background ${t.durationBase} ease;
    }
    .uwc-tabs__scroll-btn:hover {
      color:      ${t.text};
      background: ${t.hoverBg};
    }

    /* ── Panels container ────────────────────────────────────────────────── */
    .uwc-tabs__panels {
      padding: var(--uwc-tabs-panels-padding, 1.25rem 0 0);
    }

    /* ══════════════════════════════════════════════════════════════════════
       TAB POSITIONS
       CSS-driven layout shifts with the ink-bar repositioned via JS.
    ══════════════════════════════════════════════════════════════════════ */

    /* ── Bottom ──────────────────────────────────────────────────────────── */
    :host([tab-position="bottom"]) .uwc-tabs {
      flex-direction: column-reverse;
    }
    :host([tab-position="bottom"]) .uwc-tabs__nav-wrap {
      border-bottom: none;
      border-top:    1px solid var(--uwc-tabs-nav-border, ${t.border});
    }
    :host([tab-position="bottom"]) .uwc-tabs__ink-bar {
      bottom: auto;
      top:    0;
    }
    :host([tab-position="bottom"]) .uwc-tabs__panels {
      padding: 0 0 ${t.space5};
    }

    /* ── Left ────────────────────────────────────────────────────────────── */
    :host([tab-position="left"]) .uwc-tabs {
      flex-direction: row;
      align-items:    stretch;
    }
    :host([tab-position="left"]) .uwc-tabs__nav-wrap {
      flex-direction: column;
      align-items:    stretch;
      border-bottom:  none;
      border-right:   1px solid var(--uwc-tabs-nav-border, ${t.border});
      width:          var(--uwc-tabs-vertical-width, 168px);
      flex-shrink:    0;
    }
    :host([tab-position="left"]) .uwc-tabs__nav {
      flex-direction: column;
      overflow-x:     hidden;
      overflow-y:     auto;
    }
    :host([tab-position="left"]) .uwc-tabs__tab {
      justify-content: flex-start;
    }
    :host([tab-position="left"]) .uwc-tabs__ink-bar {
      /* Vertical bar on the right edge of the nav */
      bottom:     auto;
      top:        0;
      left:       auto;
      right:      -1px;
      width:      var(--uwc-tabs-ink-bar-size, 2px);
      height:     0;
    }
    :host([tab-position="left"]) .uwc-tabs__panels {
      flex:    1;
      min-width: 0;
      padding: 0 0 0 ${t.space5};
    }

    /* ── Right ───────────────────────────────────────────────────────────── */
    :host([tab-position="right"]) .uwc-tabs {
      flex-direction: row-reverse;
      align-items:    stretch;
    }
    :host([tab-position="right"]) .uwc-tabs__nav-wrap {
      flex-direction: column;
      align-items:    stretch;
      border-bottom:  none;
      border-left:    1px solid var(--uwc-tabs-nav-border, ${t.border});
      width:          var(--uwc-tabs-vertical-width, 168px);
      flex-shrink:    0;
    }
    :host([tab-position="right"]) .uwc-tabs__nav {
      flex-direction: column;
      overflow-x:     hidden;
      overflow-y:     auto;
    }
    :host([tab-position="right"]) .uwc-tabs__tab {
      justify-content: flex-start;
    }
    :host([tab-position="right"]) .uwc-tabs__ink-bar {
      /* Vertical bar on the left edge of the nav */
      bottom:     auto;
      top:        0;
      left:       -1px;
      right:      auto;
      width:      var(--uwc-tabs-ink-bar-size, 2px);
      height:     0;
    }
    :host([tab-position="right"]) .uwc-tabs__panels {
      flex:      1;
      min-width: 0;
      padding:   0 ${t.space5} 0 0;
    }

    /* ── Reduced motion ──────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-tabs__tab     { transition: none; }
      .uwc-tabs__ink-bar { transition: none; }
    }
  `,
];
